import * as React from 'react';
import { storePaymentDetailsQuery } from '../../graphql/queries';
import { useMutation, useQuery } from '@apollo/client';
import {
  AddPaymentContainer,
  PaymentIconContainer,
  PaymentLogo,
  PaymentName,
  PaymentLogoContainer,
  PaymentMethodContainer,
  PaymentSmallText,
} from './styled';
import { Check, PlusCircle } from 'react-feather';
import { Color } from '@/utils/color';
import { useContext, useState } from 'react';
import AddSepaModalWrapper from './AddSepaModal';
import ClassicLoadingSpinner from '../../components/LoadingSpinner/ClassicLoadingSpinner';
import {
  deleteSourceMutation,
  setDefaultSourceForStoreMutation,
} from '../../graphql/mutations';
import { paymentMethodTypes } from '@/utils/paymentMethods';
import AddCreditCardModalWrapper from './AddCreditCardModal';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import i18n from '@/utils/i18n';
import { FaTrashAlt } from 'react-icons/fa';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { ContentSection } from '../../components/Layout/ContentSection';
import iconSepaList from '../../images/payment/icon_sepa_list.png';
import visaMastercard from '../../images/payment/visa_mastercard.png';

const paymentMethods = [
  {
    type: paymentMethodTypes.SEPA,
    type_legacy: paymentMethodTypes.SEPA_LEGACY,
    icon: iconSepaList,
  },
  {
    type: paymentMethodTypes.CARD,
    icon: visaMastercard,
  },
];

const unknownPaymentMethod = {
  type: 'unknown',
  icon: null,
};

type StorePaymentProps = {
  storeId: string | null;
  match?: { params: { storeId: string } };
  title: string;
};

export const StorePayment = ({ storeId, match, title }: StorePaymentProps) => {
  const sId = match?.params.storeId || storeId;

  const { data = { store: null }, loading: loadingStore } = useQuery(
    storePaymentDetailsQuery,
    {
      variables: { id: sId },
      skip: !sId,
      // fetchPolicy: 'no-cache',
    },
  );
  const [setDefaultSourceForStore] = useMutation(
    setDefaultSourceForStoreMutation,
  );
  const [deleteSourceForStore] = useMutation(deleteSourceMutation);
  const [loadingSourceId, setLoadingSourceId] = useState<string | null>(null);
  const [showAddSepaModal, setShowAddSepaModal] = useState(false);
  const [showAddCreditCardModal, setShowAddCreditCardModal] = useState(false);
  const viewer = useContext(ViewerContext);
  const allowedToAccess = CanAccessPartnerData(viewer);

  const store: any = data.store || {};

  const { payment_details: paymentDetails } = store;

  async function onPaymentSourceClick(sourceId: string) {
    setLoadingSourceId(sourceId);
    await setDefaultSourceForStore({
      variables: {
        store_id: sId,
        source_id: sourceId,
      },
    });
    setLoadingSourceId(null);
  }

  async function onDeleteClick(
    e: React.MouseEvent<SVGElement, MouseEvent>,
    sourceId: string,
  ) {
    if (!allowedToAccess) {
      return;
    }
    // stop onPaymentSourceClick
    e.stopPropagation();
    setLoadingSourceId(sourceId);
    await deleteSourceForStore({
      variables: {
        user_id: sId,
        source_id: sourceId,
      },
      refetchQueries: [
        {
          query: storePaymentDetailsQuery,
          variables: { id: sId },
        },
      ],
    });
    setLoadingSourceId(null);
  }

  if (loadingStore) {
    return <LoadingContainer />;
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection title={i18n.t('Common:paymentMehtods')}>
        {paymentDetails && paymentDetails.sources.length > 0 && (
          <EditItem title={i18n.t('Payment:existingPaymentMethods')}>
            {paymentDetails.sources.map((paymentSource: any, i: number) => {
              const paymentMethod =
                paymentMethods.find(
                  (pm) =>
                    pm.type === paymentSource.type ||
                    pm.type_legacy === paymentSource.type,
                ) || unknownPaymentMethod;
              const isDefaultSource =
                paymentDetails.default_source.id === paymentSource.id;
              const isLoading = loadingSourceId === paymentSource.id;
              return (
                <PaymentMethodContainer
                  key={i}
                  onClick={() => onPaymentSourceClick(paymentSource.id)}
                >
                  <PaymentIconContainer>
                    {isDefaultSource && !isLoading && (
                      <Check color={Color.VytalGreen} />
                    )}
                    {isLoading && <ClassicLoadingSpinner />}
                  </PaymentIconContainer>
                  <PaymentName>
                    {i18n.t(`Payment:${paymentMethod.type}`)}
                    <br />
                    <PaymentSmallText>
                      ****{paymentSource.last4}
                    </PaymentSmallText>
                  </PaymentName>
                  <PaymentLogoContainer>
                    <PaymentLogo src={paymentMethod.icon || undefined} />
                  </PaymentLogoContainer>
                  {allowedToAccess ? (
                    <PaymentLogoContainer>
                      <FaTrashAlt
                        style={{ width: '2rem', marginLeft: '1rem' }}
                        onClick={(e) => onDeleteClick(e, paymentSource.id)}
                      />
                    </PaymentLogoContainer>
                  ) : null}
                </PaymentMethodContainer>
              );
            })}
          </EditItem>
        )}
        <EditItem title={i18n.t('Payment:addPaymentMethod')}>
          {paymentMethods.map((paymentMethod, i) => (
            <AddPaymentContainer
              key={i}
              onClick={() =>
                paymentMethod.type === paymentMethodTypes.SEPA
                  ? setShowAddSepaModal(true)
                  : setShowAddCreditCardModal(true)
              }
            >
              <PaymentIconContainer>
                <PlusCircle color={Color.VytalGreen} />
              </PaymentIconContainer>
              <PaymentName>
                {i18n.t(`Payment:add${paymentMethod.type}`)}
              </PaymentName>
              <PaymentLogoContainer>
                <PaymentLogo src={paymentMethod.icon} />
              </PaymentLogoContainer>
            </AddPaymentContainer>
          ))}
        </EditItem>
        <AddSepaModalWrapper
          isOpen={showAddSepaModal}
          toggle={() => setShowAddSepaModal(!showAddSepaModal)}
          storeId={sId}
        />
        <AddCreditCardModalWrapper
          isOpen={showAddCreditCardModal}
          toggle={() => setShowAddCreditCardModal(!showAddCreditCardModal)}
          storeId={sId}
        />
      </EditSection>
    </ContentSection>
  );
};
